import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, CardContent, Avatar, Chip, Button, CircularProgress, Stack,
} from '@mui/material';
import {
  Person as DoctorIcon, LocalHospital as ClinicIcon, Search as SearchIcon,
  Verified as AssignedIcon, HelpOutline as UnassignedIcon,
} from '@mui/icons-material';
import HoverCard3D from './HoverCard3D';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function AssignedCard({ entry }) {
  const navigate = useNavigate();
  const p = entry.provider;
  return (
    <HoverCard3D
      cardSx={{
        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
        border: '1px solid rgba(22, 163, 74, 0.25)',
        height: '100%',
        cursor: 'pointer',
      }}
      onClick={() => navigate('/clinics')}
    >
      <CardContent sx={{ py: 2.5, px: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#16a34a', width: 44, height: 44 }}><DoctorIcon /></Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
              <AssignedIcon sx={{ fontSize: 16, color: '#16a34a' }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: '#15803d', letterSpacing: 0.3 }}>
                YOUR DOCTOR
              </Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#14532d' }}>
              {p.full_name}
              {!p.is_active && (
                <Chip size="small" label="Inactive" sx={{ ml: 1, height: 18 }} />
              )}
            </Typography>
            {p.speciality && p.speciality.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                {p.speciality.map((s) => (
                  <Chip key={s} size="small" label={s} variant="outlined"
                    sx={{ borderColor: 'rgba(22,163,74,0.4)', color: '#15803d' }} />
                ))}
              </Box>
            )}
            {entry.clinic && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                <ClinicIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">{entry.clinic.name}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </HoverCard3D>
  );
}

function UnassignedCard({ entry }) {
  const navigate = useNavigate();
  return (
    <HoverCard3D
      cardSx={{
        background: 'linear-gradient(135deg, #fffefb 0%, #fef3c7 100%)',
        border: '1px dashed rgba(180, 83, 9, 0.35)',
        height: '100%',
      }}
    >
      <CardContent sx={{ py: 2.5, px: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#b45309', width: 44, height: 44 }}><UnassignedIcon /></Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#7c2d12' }}>
              No doctor assigned yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {entry.clinic
                ? `You're registered at ${entry.clinic.name}. A doctor will be assigned, or you can explore who's available.`
                : 'Browse doctors and clinics to find the right care for you.'}
            </Typography>
            <Button
              size="small"
              startIcon={<SearchIcon />}
              onClick={() => navigate('/clinics')}
              sx={{ mt: 1.25 }}
              variant="outlined"
              color="primary"
            >
              Find a doctor
            </Button>
          </Box>
        </Box>
      </CardContent>
    </HoverCard3D>
  );
}

export default function CareTeam({ onLoaded }) {
  const [careTeam, setCareTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/api/v1/patient/care-team`)
      .then((res) => {
        if (!active) return;
        const team = res.data?.care_team || [];
        setCareTeam(team);
        if (onLoaded) onLoaded(team);
      })
      .catch(() => { if (active) setCareTeam([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [onLoaded]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>;
  }
  if (careTeam.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}>
        Your care team
      </Typography>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mt: 0.5 }}
      >
        {careTeam.map((entry) => (
          <Box key={entry.patient_record_id} sx={{ flex: 1, minWidth: 0 }}>
            {entry.assigned ? <AssignedCard entry={entry} /> : <UnassignedCard entry={entry} />}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
